import { TechRadarApi } from '@backstage-community/plugin-tech-radar';
import {
  TechRadarLoaderResponse,
  TechRadarLoaderResponseParser,
} from '@backstage-community/plugin-tech-radar-common';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/**
 * Loads the radar from the tech-radar backend plugin.
 *
 * The radar component and the page's guide both need the response, so the
 * in-flight promise is held rather than fetched once per consumer. A failed
 * load is dropped so the next attempt retries.
 */
export class TechRadarClient implements TechRadarApi {
  private loaded?: Promise<TechRadarLoaderResponse>;

  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  async load(): Promise<TechRadarLoaderResponse> {
    this.loaded ??= this.fetchRadar().catch(error => {
      this.loaded = undefined;
      throw error;
    });

    return this.loaded;
  }

  private async fetchRadar(): Promise<TechRadarLoaderResponse> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl('tech-radar');
    const response = await this.options.fetchApi.fetch(`${baseUrl}/data`);

    if (!response.ok) {
      throw new Error(
        `Failed to load the Tech Radar (${response.status} ${response.statusText})`,
      );
    }

    // Coerces the ISO timeline dates the backend sends back into Date values.
    return TechRadarLoaderResponseParser.parse(await response.json());
  }
}
